"use client";

import { AccountsApi } from "@emilgroup/account-sdk/dist/api/accounts-api";
import { AuthserviceApi as BrokerAuthApi } from "@emilgroup/auth-sdk/dist/api/authservice-api";
import { ClaimsApi } from "@emilgroup/claim-sdk/dist/api/claims-api";
import { AuthenticationApi } from "@emilgroup/customer-sdk/dist/api/authentication-api";
import { ClaimsApi as CustomerClaimsApi } from "@emilgroup/customer-sdk/dist/api/claims-api";
import { CustomersApi } from "@emilgroup/customer-sdk/dist/api/customers-api";
import { DocumentsApi as CustomerDocumentsApi } from "@emilgroup/customer-sdk/dist/api/documents-api";
import { InvitesApi } from "@emilgroup/customer-sdk/dist/api/invites-api";
import { PoliciesApi } from "@emilgroup/customer-sdk/dist/api/policies-api";
import { DocumentsApi } from "@emilgroup/document-sdk/dist/api/documents-api";
import { LeadsApi } from "@emilgroup/insurance-sdk/dist/api/leads-api";
import { PoliciesApi as BrokerPoliciesApi } from "@emilgroup/insurance-sdk/dist/api/policies-api";
import { ProductsApi as BrokerProductsApi } from "@emilgroup/insurance-sdk/dist/api/products-api";
import { Configuration } from "@emilgroup/public-api-sdk";
import { LeadsApi as PublicLeadsApi } from "@emilgroup/public-api-sdk/dist/api/leads-api";
import { ProductsApi as PublicProductsApi } from "@emilgroup/public-api-sdk/dist/api/products-api";
import { CustomSchemaApi } from "@emilgroup/tenant-sdk/dist/api/custom-schema-api";
import { PropsWithChildren, useState } from "react";
import { getEmilEnv } from "src/queries/emil/utils";
import { Newable } from "src/types";
import { publicKey } from "./utils";
import { createContext } from "../react";
import { isSSR } from "../ssr/utils";

type AnyEmilClass =
	| CustomersApi
	| PoliciesApi
	| AuthenticationApi
	| InvitesApi
	| CustomerDocumentsApi
	| LeadsApi
	| PublicLeadsApi
	| PublicProductsApi
	| BrokerProductsApi
	| BrokerAuthApi
	| BrokerPoliciesApi
	| AccountsApi
	| DocumentsApi
	| ClaimsApi
	| CustomSchemaApi
	| CustomerClaimsApi;

const initEmilClass = <T extends AnyEmilClass>(
	c: Newable<T>,
	options?: { skipAuth?: boolean } | undefined,
) => {
	const { skipAuth } = options ?? { skipAuth: false, env: undefined };

	if (isSSR) {
		return {} as unknown as T;
	}

	const instance = new c(
		skipAuth
			? new Configuration({
					accessToken: `Bearer ${publicKey}`,
				})
			: undefined,
	);

	// this error comes from the EMIL SDK, which is using different versions of the same enum
	// @ts-expect-error ts(2769)
	instance.selectEnvironment(getEmilEnv());
	if (!skipAuth) {
		instance.loadTokenData();
	}

	return instance;
};

const initEmilApis = () => {
	return {
		customersApi: initEmilClass(CustomersApi),
		policiesApi: initEmilClass(PoliciesApi),
		authApi: initEmilClass(AuthenticationApi),
		invitesApi: initEmilClass(InvitesApi),
		customerDocumentsApi: initEmilClass(CustomerDocumentsApi),
		publicProductsApi: initEmilClass(PublicProductsApi, { skipAuth: true }),
		publicLeadsApi: initEmilClass(PublicLeadsApi, { skipAuth: true }),
		brokerAuthApi: initEmilClass(BrokerAuthApi),
		leadsApi: initEmilClass(LeadsApi),
		brokerProductsApi: initEmilClass(BrokerProductsApi),
		brokerPoliciesApi: initEmilClass(BrokerPoliciesApi),
		accountsApi: initEmilClass(AccountsApi),
		documentsApi: initEmilClass(DocumentsApi),
		brokerClaimsApi: initEmilClass(ClaimsApi),
		reset: initEmilApis,
		customSchemaApi: initEmilClass(CustomSchemaApi),
		claimsApi: initEmilClass(CustomerClaimsApi),
	};
};

type EmilContextType = ReturnType<typeof initEmilApis>;

const [EmilContext, useEmilContext] =
	createContext<EmilContextType>("EmilContext");

export const EmilProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const [value] = useState(initEmilApis);

	return (
		<EmilContext.Provider value={value}>{children}</EmilContext.Provider>
	);
};

export const useCustomersApi = () => useEmilContext().customersApi;
export const useCustomerDocumentsApi = () =>
	useEmilContext().customerDocumentsApi;
export const useAuthApi = () => useEmilContext().authApi;
export const useInvitesApi = () => useEmilContext().invitesApi;
export const usePoliciesApi = () => useEmilContext().policiesApi;
export const useBrokerAuthApi = () => useEmilContext().brokerAuthApi;
export const useLeadsApi = () => useEmilContext().leadsApi;
export const useBrokerProductsApi = () => useEmilContext().brokerProductsApi;
export const useBrokerPoliciesApi = () => useEmilContext().brokerPoliciesApi;
export const usePublicProductsApi = () => useEmilContext().publicProductsApi;
export const usePublicLeadsApi = () => useEmilContext().publicLeadsApi;
export const useAccountsApi = () => useEmilContext().accountsApi;
export const useDocumentsApi = () => useEmilContext().documentsApi;
export const useBrokerClaimsApi = () => useEmilContext().brokerClaimsApi;
export const useCustomSchemaApi = () => useEmilContext().customSchemaApi;
export const useClaimsApi = () => useEmilContext().claimsApi;

export { useEmilContext };

// 🔬 e2e
